import VStockChart from '@base/VStockChart';
import moment from 'moment';
import { API } from '@state/api';

export default {
  components: {
    VStockChart
  },
  data() {
    return {
      chartOptions: {
        lang: {
          accessibility: {
            screenReaderSection: {
              beforeRegionLabel: undefined
            }
          }
        },
        chart: {
          //zoomType: 'x',
          animation: true, // update data animation setting
          spacing: [5, 10, 4, 5] // large right spacing for y-axis label and widget drag resize zone
        },
        tooltip: {
          pointFormatter: (vm => {
            return function() {
              return vm.$f(this.y, 'float.price');
            };
          })(this),
          split: false,
          shared: true
        },
        scrollbar: {
          enabled: false
        },
        navigator: {
          enabled: false,
          adaptToUpdatedData: false
        },
        rangeSelector: {
          enabled: false
        },
        xAxis: {
          type: 'datetime',
          minRange: 3600 * 1000 * 24, // 24 hours
          labels: {
            y: 18
          },
          tickPixelInterval: 100,
          accessibility: {
            enabled: true,
            rangeDescription: undefined
          }
        },
        yAxis: {
          type: undefined, // this.yAxisType,
          // floor: 0, // The lowest allowed value for automatically computed axis extremes. Defaults to undefined.
          crosshair: {
            snap: false
          },
          labels: {
            // move labels outside of chart area
            align: 'left',
            x: 10,
            formatter: (vm => {
              return function() {
                return vm.scaleType === 'percent' ? this.value + ' %' : vm.$f(this.value, 'float.price');
              };
            })(this)
          },
          plotLines: [{ value: 0, zIndex: 3 }],
          endOnTick: false,
          startOnTick: false,
          maxPadding: 0.05,
          minPadding: 0.05,
          accessibility: {
            enabled: true,
            rangeDescription: undefined
          }
        },
        plotOptions: {
          area: {
            animation: true // initial animation setting
            // threshold: null
          },
          line: {
            animation: true, // initial animation setting
            shadow: true
          },
          ohlc: {
            animation: true // initial animation setting
          },
          candlestick: {
            animation: true // initial animation setting
          },
          series: {
            animation: true, // initial animation setting
            // highcharts doesn't react to change of compare property in plotOptions
            compare: undefined, // this.yCompareType,
            // highcharts doesn't react consistently to change of negativeColor property in plotOptions
            negativeColor: undefined,
            shadow: true,
            softThreshold: true,
            turboThreshold: Infinity
          }
        }
      },
      isChartLoading: false,
      isChartData: false,
      timeseries: {
        count: 0,
        xy: [], // x y format
        xohlc: [] // x ohlc format
      }
    };
  },
  computed: {
    yCompareType() {
      return this.scaleType === 'percent' ? 'percent' : '';
    },
    yAxisType() {
      return this.scaleType === 'percent' ? 'linear' : this.scaleType;
    },
    scaleTypeOptions() {
      return [
        { value: 'linear', text: 'Abs' },
        { value: 'percent', text: 'Rel' },
        { value: 'logarithmic', text: 'Log' }
      ];
    },
    timeRangeOptions() {
      const path = 'white-label.globalLabels.chart.period.options.';
      return [
        { value: '1D', text: this.$t(path + '1d') },
        { value: '2D', text: this.$t(path + '2d') },
        { value: '1W', text: this.$t(path + '1w') },
        { value: '2W', text: this.$t(path + '2w') },
        { value: '1M', text: this.$t(path + '1m') },
        { value: '3M', text: this.$t(path + '3m') },
        { value: 'YTD', text: this.$t(path + 'ytd') },
        { value: '1Y', text: this.$t(path + '1y') },
        { value: '2Y', text: this.$t(path + '2y') },
        { value: '10Y', text: this.$t(path + '10y') },
        { value: 'Max', text: this.$t(path + 'max') }
      ];
    }
  },
  watch: {
    scaleType: {
      handler(newVal, oldVal) {
        if (newVal === oldVal) return;
        this.updateChart();
      },
      immediate: true
    },
    timeRange() {
      this.chartZoomReset();
      this.fetchChartData();
    }
  },
  methods: {
    chartZoomReset() {
      this.$refs.chart.getChart().zoom();
    },
    getStartDateMoment(lastTradeDate) {
      const quanti = this.timeRange.match(/^(\d+)?(\w)$/);
      if (quanti) {
        let count = quanti[1] ? +quanti[1] : 1;
        const period = quanti[2] === 'M' ? 'M' : quanti[2].toLowerCase();
        if (period === 'd') count--;
        const date = moment(lastTradeDate).subtract(count, period);
        if (date.isoWeekday() > 5) date.isoWeekday(5);
        return date;
      }
      if (this.timeRange === 'YTD') {
        return moment().startOf('year');
      }
      return moment().subtract(20, 'y');
    },
    getUTCTimestamp(date) {
      return Date.parse(new Date(date).toUTCString());
    },
    clearTimeSeriesData() {
      this.timeseries.count = 0;
      this.timeseries.xy = [];
      this.timeseries.xohlc = [];
    },
    updateHighChartsData() {
      if (this.$refs.chart) {
        this.$refs.chart.update(
          {
            series: ['ohlc', 'candlestick'].includes(this.chartType) ? this.timeseries.xohlc : this.timeseries.xy
          },
          true,
          true
        ); // options: [redraw, oneToOne, animation]
      }
    },
    async fetchChartData() {
      this.isChartLoading = true;
      this.isChartData = false;
      const tradeDateRes = await API.Rest.get(`/quote/info?rics=${this.ric}&fids=_TRADE_DATE`);
      if (tradeDateRes.data.data.length > 0) {
        const lastTradeDate = new Date(tradeDateRes.data.data[0]._TRADE_DATE);
        const startDateMoment = this.getStartDateMoment(lastTradeDate);
        const daysRange = moment(lastTradeDate).diff(startDateMoment, 'days');
        const isIntraday = daysRange < 32;
        // for day chart request 5min intervals
        let minutesInterval = daysRange > 0 ? 15 : 5;
        let sampleUnit = 'D';

        if (['line', 'area'].includes(this.chartType)) {
          switch (this.timeRange) {
            case 'W':
            case '2W':
            case 'M':
            case 'YTD':
            case 'Y':
            case '2Y':
              sampleUnit = 'D';
              break;
            case '10Y':
            case 'Max':
              sampleUnit = 'W';
          }
        } else if (['ohlc', 'candlestick'].includes(this.chartType)) {
          switch (this.timeRange) {
            case 'W':
            case '2W':
            case 'M':
              minutesInterval = 60;
              break;
            case 'YTD':
            case 'Y':
              sampleUnit = 'W';
              break;
            case '2Y':
              sampleUnit = 'M';
              break;
            case '10Y':
            case 'Max':
              sampleUnit = 'Q';
          }
        }

        this.isChartData = true;
        this.addTimeSeriesData(isIntraday, startDateMoment, minutesInterval, sampleUnit).then(() => {
          this.updateHighChartsData();
          this.isChartLoading = false;
        });
      } else {
        this.isChartLoading = false;
      }
      this.updateChart();
    },
    updateChart() {
      if (this.$refs.chart) {
        this.$refs.chart.update(
          {
            series: [
              {
                compare: this.yCompareType,
                negativeColor: this.scaleType === 'percent'
              }
            ],
            yAxis: [
              {
                type: this.yAxisType
              }
            ]
          },
          true
        );
      } else {
        this.chartOptions.plotOptions.series.negativeColor = this.scaleType === 'percent';
        this.chartOptions.yAxis.type = this.yAxisType;
        this.chartOptions.plotOptions.series.compare = this.yCompareType;
      }
    },
    async getIntradayData(startDateMoment, minutesInterval) {
      const intradayResponse = await API.Rest.get(
        `/timeseries/intraday` +
          `?ric=${this.ric}` +
          `&fids=TIME,OPEN,HIGH,LOW,CLOSE` +
          `&fromDate=${startDateMoment.format('YYYY-MM-DD')}` +
          `&interval=${minutesInterval}`
      );
      const intradayResponseData = intradayResponse.data.data;
      return intradayResponseData;
    },
    async getHistoricalData(startDateMoment, sampleUnit) {
      const samples = sampleUnit || 'D'; // D = Daily, W = Weekly,M = Monthly, Q = quarterly, Y = yearly
      const appendRecentData = 'all'; // all appends the latest tick of the current day
      const historicalResponse = await API.Rest.get(
        `/timeseries/historical` +
          `?ric=${this.ric}` +
          `&fids=_DATE_END,OPEN_PRC,CLOSE_PRC,LOW_1,HIGH_1` +
          `&fromDate=${startDateMoment.format('YYYY-MM-DD')}` +
          // `&toDate=${moment(endDate).format('YYYY-MM-DD')}` +
          `&samples=${samples}` +
          `&appendRecentData=${appendRecentData}`
      );
      const historicalResponseData = historicalResponse.data.data;
      return historicalResponseData;
    },
    async addTimeSeriesData(isIntraday, startDate, minutesInterval, sampleUnit) {
      try {
        const dataSeries = isIntraday
          ? await this.getIntradayData(startDate, minutesInterval)
          : await this.getHistoricalData(startDate, sampleUnit);

        // line, area
        const timeSeriesXY = {
          // colorIndex: this.timeseries.xy.length,
          id: this.ric,
          name: this.ric,
          data: dataSeries.map(item => {
            return {
              x: isIntraday ? item.TIME * 1000 : this.getUTCTimestamp(item._DATE_END),
              y: isIntraday ? parseFloat(item.CLOSE) : parseFloat(item.CLOSE_PRC)
            };
          }),
          dataGrouping: {
            //enabled: false
          },
          type: this.chartType,
          softThreshold: false
        };

        // candlestick, ohlc
        const timeSeriesXOHLC = {
          // colorIndex: this.timeseries.xohlc.length,
          id: this.ric,
          name: this.ric,
          data: dataSeries.map(item => {
            return {
              x: isIntraday ? item.TIME * 1000 : this.getUTCTimestamp(item._DATE_END),
              open: isIntraday ? parseFloat(item.OPEN) : parseFloat(item.OPEN_PRC),
              high: isIntraday ? parseFloat(item.HIGH) : parseFloat(item.HIGH_1),
              low: isIntraday ? parseFloat(item.LOW) : parseFloat(item.LOW_1),
              close: isIntraday ? parseFloat(item.CLOSE) : parseFloat(item.CLOSE_PRC)
            };
          }),
          dataGrouping: {
            enabled: false
          },
          type: this.chartType
        };

        this.timeseries.xy[0] = timeSeriesXY;
        this.timeseries.xohlc[0] = timeSeriesXOHLC;
        this.timeseries.count = 1;
      } catch (e) {
        console.error(e);
        return [];
      }
    }
  }
};
